import React from 'react';
import Icon from '../../../components/AppIcon';
import ProgressRing from '../../progress-dashboard/components/ProgressRing';

const ProbabilityCalculator = ({ data, loading = false }) => {
  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-muted rounded w-1/4 mb-4" />
            <div className="h-32 bg-muted rounded mb-4" />
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const probabilityScore = data?.probabilityScore || 0;
  const factorAnalysis = data?.factorAnalysis || [];
  const improvementStrategies = data?.improvementStrategies || [];
  const bestTimeframes = data?.bestTimeframes || [];
  const riskFactors = data?.riskFactors || [];

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Very High';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Moderate';
    return 'Low';
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'positive': return 'text-success';
      case 'negative': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getImpactIcon = (impact) => {
    switch (impact) {
      case 'positive': return 'TrendingUp';
      case 'negative': return 'TrendingDown';
      default: return 'Minus';
    }
  };

  return (
    <div className="space-y-6">
      {/* Overall Probability */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Calculator" size={16} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Success Probability Analysis</h3>
        </div>

        <div className="text-center mb-6">
          <ProgressRing
            percentage={probabilityScore}
            title={`${probabilityScore}%`}
            subtitle={getScoreLabel(probabilityScore)}
            size="lg"
            className="mx-auto mb-4"
          />
          <p className={`text-lg font-semibold ${getScoreColor(probabilityScore)}`}>
            {getScoreLabel(probabilityScore)} Success Probability
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Based on current factors and market conditions
          </p>
        </div>
      </div>

      {/* Factor Analysis */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
            <Icon name="BarChart3" size={16} className="text-secondary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Impact Factors</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {factorAnalysis?.map((factor, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={getImpactIcon(factor?.impact)} 
                    size={16} 
                    className={getImpactColor(factor?.impact)} 
                  />
                  <h4 className="font-medium text-foreground">{factor?.factor}</h4>
                </div>
                <span className="text-sm font-medium text-foreground">
                  {Math.round(factor?.weight * 100)}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full ${
                    factor?.impact === 'positive' ? 'bg-success' :
                    factor?.impact === 'negative'? 'bg-destructive' : 'bg-muted-foreground'
                  }`}
                  style={{ width: `${factor?.weight * 100}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {factor?.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Best Timeframes */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center">
            <Icon name="Calendar" size={16} className="text-warning" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Optimal Timing Windows</h3>
        </div>

        <div className="space-y-4">
          {bestTimeframes?.map((timeframe, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-foreground">{timeframe?.period}</h4>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm font-medium ${getScoreColor(timeframe?.probability)}`}>
                    {timeframe?.probability}% success rate
                  </span>
                  <div className={`w-3 h-3 rounded-full ${
                    timeframe?.probability >= 80 ? 'bg-success' :
                    timeframe?.probability >= 60 ? 'bg-warning': 'bg-destructive'
                  }`} />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {timeframe?.reasoning}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Improvement Strategies */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Target" size={16} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Improvement Strategies</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {improvementStrategies?.map((strategy, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-foreground">{strategy?.strategy}</h4>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  strategy?.effort === 'low' ? 'bg-success/20 text-success' :
                  strategy?.effort === 'medium'? 'bg-warning/20 text-warning' : 'bg-destructive/20 text-destructive'
                }`}>
                  {strategy?.effort} effort
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                {strategy?.potentialImpact}
              </p>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Clock" size={14} />
                <span>{strategy?.timeframe}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Factors */}
      {riskFactors?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-destructive/10 rounded-full flex items-center justify-center">
              <Icon name="AlertTriangle" size={16} className="text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Risk Factors & Mitigation</h3>
          </div>

          <div className="space-y-4">
            {riskFactors?.map((risk, index) => (
              <div key={index} className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-foreground flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={14} className="text-destructive" />
                    <span>{risk?.risk}</span>
                  </h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    risk?.severity === 'low' ? 'bg-success/20 text-success' :
                    risk?.severity === 'medium'? 'bg-warning/20 text-warning' : 'bg-destructive/20 text-destructive'
                  }`}>
                    {risk?.severity} risk
                  </span>
                </div>
                <div className="bg-card rounded-lg p-3 border-l-4 border-primary">
                  <p className="text-sm text-muted-foreground">
                    <strong>Mitigation:</strong> {risk?.mitigation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProbabilityCalculator;